import { cn } from "@/components/podkit/lib/cn";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useOpenEditor } from "@/hooks/use-open-editor";
import { useStartEnvironment, useStopEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { DeleteEnvironmentModal, ForceDeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { canOpen, environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import { useEnvironmentSupportBundle } from "@/hooks/use-environment-support-bundle";
import { useLifetimeEnforcement } from "@/hooks/use-lifetime-enforcement";
import { formatError } from "@/utils/errors";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useIsPointerCoarse } from "@/hooks/use-is-pointer-coarse";
import type { PlainEditor } from "@/queries/editor-queries";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import {
    ContextMenuItem,
    ContextMenuRoot,
    ContextMenuTrigger,
    ContextMenuPortal,
    ContextMenuContent,
    ContextMenuSeparator,
} from "@/components/flexkit/ContextMenu";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";

export const EnvironmentActionsMenu: FC<{
    environment: PlainEnvironment;
    handleViewDetails?: () => void;
    defaultEditor: PlainEditor | undefined;
    startRename: (() => void) | undefined;
    children: React.ReactNode;
    renderAs: "dropdownMenu" | "contextMenu";
    onOpenChange?: (open: boolean) => void;
    agentExecution?: PlainAgentExecution;
    open?: boolean;
}> = ({
    environment,
    handleViewDetails,
    renderAs,
    children,
    defaultEditor,
    startRename,
    onOpenChange,
    agentExecution,
    open,
}) => {
    const { toast } = useToast();
    const navigate = useNavigate();

    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { data: runner } = useRunner(environment.metadata?.runnerId);

    const { downloadSupportBundle, isSupportBundleAvailable, enableTokenFetch, isTokenReady } =
        useEnvironmentSupportBundle(environment);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const { enforcementEnabled, handleStartError, modalProps } = useLifetimeEnforcement();

    const allowForceDelete = runner && runner.provider !== RunnerProvider.MANAGED;

    const handleOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                enableTokenFetch();
            }
            onOpenChange?.(isOpen);
        },
        [enableTokenFetch, onOpenChange],
    );

    const handleViewLogs = useCallback(() => {
        navigate(`/details/${environment.id}/logs`);
    }, [environment.id, navigate]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({
                environmentId: environment.id,
                skipPolicyCheck: enforcementEnabled,
            });
        } catch (error) {
            if (handleStartError(error)) {
                return;
            }
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, enforcementEnabled, handleStartError, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    const openEditor = useOpenEditor();
    const handleStop = useCallback(async () => {
        try {
            await stopEnvironment.mutateAsync(environment.id);
        } catch (error) {
            toast({
                title: "Failed to stop environment",
                description: formatError(error),
            });
        }
    }, [stopEnvironment, toast, environment.id]);

    const handleDelete = useCallback(() => setShowDeleteModal(true), [setShowDeleteModal]);
    const handleForceDelete = useCallback(() => setShowForceDeleteModal(true), [setShowForceDeleteModal]);

    const isPointerCoarse = useIsPointerCoarse();

    const Root = renderAs === "dropdownMenu" ? DropdownMenu : ContextMenuRoot;
    const Trigger = renderAs === "dropdownMenu" ? DropdownMenuTrigger : ContextMenuTrigger;
    const MenuItem = renderAs === "dropdownMenu" ? DropdownMenuItem : ContextMenuItem;
    const Content = renderAs === "dropdownMenu" ? DropdownMenuContent : ContextMenuContent;
    const Portal = renderAs === "dropdownMenu" ? "div" : ContextMenuPortal; // dropdownMenu already has a portal
    const Separator = renderAs === "dropdownMenu" ? DropdownMenuSeparator : ContextMenuSeparator;

    // On touch devices, disable the Radix context menu trigger to prevent its
    // built-in 700ms long-press from firing during scroll gestures and locking
    // the page. The dropdown menu (via react-aria useLongPress) handles mobile
    // long-press instead.
    const disableContextTrigger = renderAs === "contextMenu" && isPointerCoarse;

    return (
        <>
            <Root modal={true} onOpenChange={handleOpenChange} open={open}>
                {renderAs === "dropdownMenu" ? (
                    <Trigger asChild>{children}</Trigger>
                ) : (
                    <Trigger disabled={disableContextTrigger}>{children}</Trigger>
                )}
                <Portal>
                    {/* DropdownMenuItem has mx-1 built-in, ContextMenuItem doesn't - use px-0 for dropdown to match */}
                    <Content
                        className={cn("min-w-[220px] rounded-md bg-surface-01 shadow-lg p-1", {
                            "px-0 py-1": renderAs === "dropdownMenu",
                        })}
                    >
                        <MenuItem
                            onClick={() => defaultEditor && openEditor(defaultEditor, environment)}
                            disabled={!canOpen(environment)}
                            data-tracking-id="open-environment-environment-actions-menu"
                        >
                            Open
                        </MenuItem>
                        <MenuItem
                            onClick={handleCopyId}
                            data-tracking-id="copy-environment-id-environment-actions-menu"
                        >
                            Copy ID
                        </MenuItem>
                        <Separator />
                        <MenuItem
                            onClick={handleViewLogs}
                            data-testid="environment-actions-dropdown-view-logs"
                            data-tracking-id="view-environment-logs-environment-actions-menu"
                        >
                            View Logs
                        </MenuItem>
                        {isSupportBundleAvailable && (
                            <MenuItem
                                onClick={downloadSupportBundle}
                                disabled={!isTokenReady}
                                data-testid="environment-actions-menu-download-support-bundle"
                                data-tracking-id="download-support-bundle-environment-actions-menu"
                            >
                                Download support bundle
                            </MenuItem>
                        )}
                        <Separator />
                        {handleViewDetails && (
                            <MenuItem
                                onClick={handleViewDetails}
                                data-tracking-id="view-environment-details-environment-actions-menu"
                            >
                                View Details
                            </MenuItem>
                        )}
                        <MenuItem onClick={startRename} data-tracking-id="rename-environment-environment-actions-menu">
                            Rename
                        </MenuItem>
                        <MenuItem
                            onClick={handleStart}
                            disabled={!showStart(environment) || !environmentHasReachedStablePhase(environment)}
                            data-testid="environment-actions-dropdown-start"
                            data-tracking-id="start-environment-environment-actions-menu"
                        >
                            Start
                        </MenuItem>
                        <MenuItem
                            onClick={handleStop}
                            disabled={!showStop(environment)}
                            data-testid="environment-actions-dropdown-stop"
                            data-tracking-id="stop-environment-environment-actions-menu"
                        >
                            Stop
                        </MenuItem>
                        {/* Show Delete, when the Environment has not yet been requested to delete */}
                        {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                            <MenuItem
                                variant="destructive"
                                onClick={handleDelete}
                                data-tracking-id="delete-environment-environment-actions-menu"
                            >
                                Delete
                            </MenuItem>
                        )}
                        {/* Show Force Delete, when the Environment has been requested to delete and is not on a managed runner */}
                        {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && allowForceDelete && (
                            <MenuItem
                                onClick={handleForceDelete}
                                variant="destructive"
                                data-tracking-id="force-delete-environment-environment-actions-menu"
                            >
                                Force Delete
                            </MenuItem>
                        )}
                    </Content>
                </Portal>
            </Root>
            <DeleteEnvironmentModal
                environment={environment}
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                agentExecution={agentExecution}
            />
            <ForceDeleteEnvironmentModal
                environment={environment}
                open={showForceDeleteModal}
                onClose={() => setShowForceDeleteModal(false)}
                agentExecution={agentExecution}
            />
            <EnvironmentLifetimeExceededModal environment={environment} {...modalProps} />
        </>
    );
};
